import React, { useEffect, useMemo, useState } from "react";
import api from "../../../utilis/api";
import { server } from "../../../utilis/constant";
import { toast } from "react-toastify";

const createEmptyForm = () => ({
  title: "",
  description: "",
  category: "Fragrance",
  quantity: "",
  sku: "",
  barCode: "",
  type: "Beauty",
  vendor: "",
  tags: "",
  variants: [],
});

const createEmptyVariant = () => ({
  title: "",
  qty: "",
  price: "",
  image: "",
  imageFile: null,
});

const Field = ({ label, hint, children }) => (
  <label className="flex flex-col gap-2 text-sm">
    <span className="flex items-center justify-between gap-3 text-zinc-200">
      <span className="font-medium">{label}</span>
      {hint && <span className="text-xs text-zinc-500">{hint}</span>}
    </span>
    {children}
  </label>
);

const ProductForm = ({ initialData = null, onSubmit, onCancel }) => {
  const isEdit = Boolean(initialData?._id);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(createEmptyForm);
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    if (!initialData) {
      setForm(createEmptyForm());
      setImageFile(null);
      return;
    }

    setForm({
      ...createEmptyForm(),
      ...initialData,
      quantity: initialData.quantity ?? "",
      tags: Array.isArray(initialData.tags) ? initialData.tags.join(", ") : "",
      variants:
        initialData.variants?.map((variant) => ({
          ...variant,
          qty: variant.qty ?? "",
          price: variant.price ?? "",
          imageFile: null,
        })) || [],
    });
    setImageFile(null);
  }, [initialData]);

  const mainImagePreview = useMemo(() => {
    if (imageFile) return URL.createObjectURL(imageFile);
    if (form.image) return `${server}/${form.image}`;
    return "";
  }, [imageFile, form.image]);

  const handleChange = (event) => {
    setForm((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const addVariant = () => {
    setForm((prev) => ({
      ...prev,
      variants: [...prev.variants, createEmptyVariant()],
    }));
  };

  const updateVariant = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.map((variant, variantIndex) =>
        variantIndex === index ? { ...variant, [field]: value } : variant,
      ),
    }));
  };

  const removeVariant = (index) => {
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.filter((_, variantIndex) => variantIndex !== index),
    }));
  };

  const buildPayload = () => {
    const formData = new FormData();
    const tagsArray =
      typeof form.tags === "string"
        ? form.tags
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean)
        : form.tags || [];

    formData.append("title", form.title.trim());
    formData.append("description", form.description || "");
    formData.append("category", form.category || "Fragrance");
    formData.append("quantity", Number(form.quantity || 0));
    formData.append("sku", form.sku || "");
    formData.append("barCode", form.barCode || "");
    formData.append("vendor", form.vendor || "");
    formData.append("type", form.type || "Beauty");
    formData.append("tags", JSON.stringify(tagsArray));

    if (imageFile) {
      formData.append("image", imageFile);
    }

    const variantData = form.variants.map((variant) => ({
      _id: variant._id,
      title: variant.title,
      qty: Number(variant.qty || 0),
      price: Number(variant.price || 0),
      image: variant.image || null,
    }));

    formData.append("variants", JSON.stringify(variantData));

    form.variants.forEach((variant, index) => {
      if (variant.imageFile) {
        formData.append(`variantImage_${index}`, variant.imageFile);
      }
    });

    return formData;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;

    if (!form.title.trim()) {
      toast.error("Product title is required");
      return;
    }

    setLoading(true);

    try {
      const payload = buildPayload();
      const config = { headers: { "Content-Type": "multipart/form-data" } };

      if (isEdit) {
        await api.put(`/products/${initialData._id}`, payload, config);
        toast.success("Product updated successfully");
      } else {
        await api.post("/products", payload, config);
        toast.success("Product created successfully");
        setForm(createEmptyForm());
        setImageFile(null);
      }

      onSubmit && onSubmit();
    } catch (error) {
      console.error(error);
      toast.error(isEdit ? "Update failed" : "Create failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-[#0f0f14] text-white border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
    >
      <div className="sticky top-0 z-10 bg-[#0f0f14]/95 backdrop-blur border-b border-white/10 px-5 md:px-8 py-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              Product Catalog
            </p>
            <h2 className="text-xl md:text-2xl font-semibold mt-1">
              {isEdit ? "Edit Product" : "Add Product"}
            </h2>
          </div>

          <div className="flex items-center gap-2">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 rounded-xl border border-white/10 text-zinc-300 hover:bg-white/10 transition text-sm"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 rounded-xl bg-white text-black font-semibold hover:bg-zinc-200 disabled:opacity-60 disabled:cursor-not-allowed transition text-sm"
            >
              {loading ? "Saving..." : isEdit ? "Update Product" : "Create Product"}
            </button>
          </div>
        </div>
      </div>

      <div className="p-5 md:p-8 space-y-6">
        <section className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <p className="font-semibold text-zinc-100">Product Image</p>
            <p className="text-xs text-zinc-500 mt-1">
              Use a clear square image for best catalog display.
            </p>

            <div className="mt-4 aspect-square rounded-2xl border border-dashed border-white/15 bg-black/40 overflow-hidden flex items-center justify-center">
              {mainImagePreview ? (
                <img
                  src={mainImagePreview}
                  alt={form.title || "Product preview"}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-sm text-zinc-500">No image selected</span>
              )}
            </div>

            <input
              type="file"
              accept="image/*"
              onChange={(event) => setImageFile(event.target.files?.[0] || null)}
              className="mt-4 block w-full text-sm text-zinc-400 file:mr-3 file:rounded-lg file:border-0 file:bg-white file:px-3 file:py-2 file:text-sm file:font-semibold file:text-black hover:file:bg-zinc-200"
            />
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 md:p-5">
            <div className="flex items-center justify-between gap-4 mb-5">
              <div>
                <h3 className="font-semibold text-zinc-100">Core Details</h3>
                <p className="text-xs text-zinc-500 mt-1">
                  These fields power the storefront listing and product page.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Product title">
                <input
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="e.g. Amber Oud Eau De Parfum"
                  className="bg-zinc-950 border border-white/10 p-3 rounded-xl outline-none focus:ring-2 focus:ring-white/20"
                />
              </Field>

              <Field label="Vendor / brand">
                <input
                  name="vendor"
                  value={form.vendor}
                  onChange={handleChange}
                  placeholder="e.g. Gift of God Fragrances"
                  className="bg-zinc-950 border border-white/10 p-3 rounded-xl outline-none focus:ring-2 focus:ring-white/20"
                />
              </Field>

              <Field label="Category">
                <select
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  className="bg-zinc-950 border border-white/10 p-3 rounded-xl outline-none focus:ring-2 focus:ring-white/20"
                >
                  <option value="Fragrance">Fragrance</option>
                  <option value="Skincare">Skincare</option>
                  <option value="Beauty">Beauty</option>
                </select>
              </Field>

              <Field label="Type">
                <input
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  placeholder="Beauty"
                  className="bg-zinc-950 border border-white/10 p-3 rounded-xl outline-none focus:ring-2 focus:ring-white/20"
                />
              </Field>

              <Field label="Total quantity">
                <input
                  name="quantity"
                  type="number"
                  min="0"
                  value={form.quantity}
                  onChange={handleChange}
                  placeholder="0"
                  className="bg-zinc-950 border border-white/10 p-3 rounded-xl outline-none focus:ring-2 focus:ring-white/20"
                />
              </Field>

              <Field label="SKU">
                <input
                  name="sku"
                  value={form.sku}
                  onChange={handleChange}
                  placeholder="Internal stock code"
                  className="bg-zinc-950 border border-white/10 p-3 rounded-xl outline-none focus:ring-2 focus:ring-white/20"
                />
              </Field>

              <Field label="Barcode">
                <input
                  name="barCode"
                  value={form.barCode}
                  onChange={handleChange}
                  placeholder="UPC / barcode"
                  className="bg-zinc-950 border border-white/10 p-3 rounded-xl outline-none focus:ring-2 focus:ring-white/20"
                />
              </Field>

              <Field label="Tags" hint="Comma separated">
                <input
                  name="tags"
                  value={form.tags}
                  onChange={handleChange}
                  placeholder="Men, Best Sales, New Arrivals"
                  className="bg-zinc-950 border border-white/10 p-3 rounded-xl outline-none focus:ring-2 focus:ring-white/20"
                />
              </Field>
            </div>

            <Field label="Description" hint="Shown on product detail page">
              <textarea
                name="description"
                rows={5}
                value={form.description}
                onChange={handleChange}
                placeholder="Describe scent notes, size, concentration, and other useful buying details."
                className="bg-zinc-950 border border-white/10 p-3 rounded-xl outline-none focus:ring-2 focus:ring-white/20 resize-y mt-4"
              />
            </Field>
          </div>
        </section>

        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 md:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="font-semibold text-zinc-100">Variants</h3>
              <p className="text-xs text-zinc-500 mt-1">
                Add each sellable size or option with its own price, stock, and image.
              </p>
            </div>

            <button
              type="button"
              onClick={addVariant}
              className="px-4 py-2 rounded-xl bg-white text-black font-semibold hover:bg-zinc-200 transition text-sm"
            >
              Add Variant
            </button>
          </div>

          {form.variants.length === 0 ? (
            <div className="mt-5 rounded-xl border border-dashed border-white/15 bg-black/30 px-5 py-8 text-center">
              <p className="text-sm font-medium text-zinc-300">No variants yet</p>
              <p className="text-xs text-zinc-500 mt-1">
                Add a variant to set price, quantity, and optional variant image.
              </p>
            </div>
          ) : (
            <div className="mt-5 space-y-3">
              {form.variants.map((variant, index) => {
                const variantPreview = variant.imageFile
                  ? URL.createObjectURL(variant.imageFile)
                  : variant.image
                    ? `${server}/${variant.image}`
                    : "";

                return (
                  <div
                    key={variant._id || index}
                    className="grid grid-cols-1 lg:grid-cols-[72px_1fr_120px_120px_220px_auto] gap-3 items-center rounded-2xl border border-white/10 bg-zinc-950/80 p-3"
                  >
                    <div className="w-[72px] h-[72px] rounded-xl overflow-hidden bg-black border border-white/10 flex items-center justify-center">
                      {variantPreview ? (
                        <img
                          src={variantPreview}
                          alt={variant.title || `Variant ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-xs text-zinc-600">Image</span>
                      )}
                    </div>

                    <input
                      value={variant.title}
                      onChange={(event) =>
                        updateVariant(index, "title", event.target.value)
                      }
                      placeholder="Variant title, e.g. 100ml"
                      className="bg-black border border-white/10 p-3 rounded-xl outline-none focus:ring-2 focus:ring-white/20"
                    />

                    <input
                      type="number"
                      min="0"
                      value={variant.qty}
                      onChange={(event) =>
                        updateVariant(index, "qty", event.target.value)
                      }
                      placeholder="Qty"
                      className="bg-black border border-white/10 p-3 rounded-xl outline-none focus:ring-2 focus:ring-white/20"
                    />

                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={variant.price}
                      onChange={(event) =>
                        updateVariant(index, "price", event.target.value)
                      }
                      placeholder="Price"
                      className="bg-black border border-white/10 p-3 rounded-xl outline-none focus:ring-2 focus:ring-white/20"
                    />

                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) =>
                        updateVariant(index, "imageFile", event.target.files?.[0] || null)
                      }
                      className="block w-full text-xs text-zinc-400 file:mr-2 file:rounded-lg file:border-0 file:bg-white/10 file:px-3 file:py-2 file:text-xs file:font-medium file:text-white hover:file:bg-white/20"
                    />

                    <button
                      type="button"
                      onClick={() => removeVariant(index)}
                      className="h-10 px-3 rounded-xl bg-red-500/10 text-red-300 hover:bg-red-500/20 transition text-sm"
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <div className="sticky bottom-0 bg-[#0f0f14]/95 backdrop-blur border-t border-white/10 px-5 md:px-8 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-xs text-zinc-500">
            {form.variants.length} variant{form.variants.length === 1 ? "" : "s"} configured
          </p>

          <div className="flex justify-end gap-2">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 rounded-xl border border-white/10 text-zinc-300 hover:bg-white/10 transition text-sm"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 rounded-xl bg-white text-black font-semibold hover:bg-zinc-200 disabled:opacity-60 disabled:cursor-not-allowed transition text-sm"
            >
              {loading ? "Saving..." : isEdit ? "Update Product" : "Create Product"}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ProductForm;
